import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, MapPin } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Loading, Table, Title, fmtDT, useData } from "@/ops/ui";
import { mapsLink } from "@/ops/filters";

const POL = [["reject", "Okutmayı reddet"], ["flag", "Kaydet, uyarılı işaretle"]];
const NUMS = [["default_radius_m", "Varsayılan kabul mesafesi (metre)", "Kontrol noktası veya projede özel mesafe yoksa bu değer kullanılır."],
  ["max_accuracy_m", "GPS doğruluk sınırı (metre)", "Telefonun bildirdiği doğruluk bu değerden kötüyse 'düşük doğruluk' sayılır."]];
const POLS = [["no_location_policy", "Konum alınamazsa / izin yoksa"], ["outside_policy", "Kabul mesafesi dışındaysa"], ["low_accuracy_policy", "GPS doğruluğu sınırın üstündeyse"]];

function SettingsForm() {
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get("/ops/admin/settings/gps").then((r) => setS(r.data)).catch((e) => toast.error(errMsg(e)));
  }, []);
  if (!s) return <Loading />;
  const save = async () => {
    const r = Number(s.default_radius_m), a = Number(s.max_accuracy_m);
    if (!(r >= 10 && r <= 2000)) return toast.error("Kabul mesafesi 10–2000 metre arasında olmalıdır.");
    if (!(a >= 5 && a <= 1000)) return toast.error("GPS doğruluk sınırı 5–1000 metre arasında olmalıdır.");
    try {
      const { defaults, updated_at, updated_by, ...body } = s;
      setS((await api.put("/ops/admin/settings/gps", { ...body, default_radius_m: Number(body.default_radius_m), max_accuracy_m: Number(body.max_accuracy_m) })).data);
      toast.success("Ayarlar kaydedildi ve okutma doğrulamasına uygulandı");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="grid gap-4 rounded-lg border border-zinc-200 bg-white p-5 md:grid-cols-2" data-testid="gps-settings-form">
      {NUMS.map(([k, l, h]) => <label key={k} className="grid gap-1 text-sm font-semibold">{l}<input type="number" min={k === "default_radius_m" ? 10 : 5} max={k === "default_radius_m" ? 2000 : 1000} value={s[k]} onChange={(e) => setS({ ...s, [k]: e.target.value })} className="field-input" data-testid={`gps-${k}`} /><span className="text-xs font-normal text-zinc-500">{h} Varsayılan: {s.defaults[k]} m</span></label>)}
      {POLS.map(([k, l]) => <label key={k} className="grid gap-1 text-sm font-semibold">{l}<select value={s[k]} onChange={(e) => setS({ ...s, [k]: e.target.value })} className="field-input" data-testid={`gps-${k}`}>{POL.map(([v, t]) => <option key={v} value={v}>{t}</option>)}</select></label>)}
      <div className="flex items-end justify-between gap-3 md:col-span-2">
        <p className="text-xs text-zinc-500">Uyarılı kaydedilen konumsuz veya mesafe dışı okutmalar devriyeyi tamamlamış sayılmaz; düşük doğruluk yalnızca uyarı olarak işaretlenir. {s.updated_at ? `Son değişiklik: ${fmtDT(s.updated_at)} · ${s.updated_by}` : "Henüz değiştirilmedi (varsayılanlar geçerli)."}</p>
        <button onClick={save} className="btn-red !py-2" data-testid="gps-save-btn"><Save className="h-4 w-4" /> Kaydet</button>
      </div>
    </div>
  );
}

export default function GpsSettings() {
  const [cps] = useData("/ops/admin/checkpoints/unlocated");
  return (
    <div>
      <Title title="GPS ve Devriye Ayarları" desc="Bu ayarlar personelin her QR okutmasında sunucu tarafında uygulanır. Her değişiklik denetim kaydına yazılır." />
      <SettingsForm />
      <h2 className="mb-2 mt-8 text-lg font-bold">Kontrol noktalarının konum durumu</h2>
      {!cps ? <Loading /> : (
        <Table head={["Proje", "Nokta", "Konum", "Son kayıt", "Kaydeden", ""]} testid="gps-checkpoints-table">
          {cps.map((c) => (
            <tr key={c.id}><td className="px-3 py-2">{c.project_name}</td><td className="px-3 py-2 font-semibold">{c.name}</td>
              <td className="px-3 py-2 text-xs">{c.lat != null ? <a className="text-blue-700 underline" href={mapsLink(c.lat, c.lng)} target="_blank" rel="noreferrer">{c.lat.toFixed(5)}, {c.lng.toFixed(5)}</a> : <span className="font-semibold text-orange-700">{c.uses_project_location ? "Kaydedilmedi (proje konumu kullanılıyor)" : "Kaydedilmedi – mesafe doğrulanamaz"}</span>}</td>
              <td className="px-3 py-2 text-xs">{fmtDT(c.loc_set_at)}</td><td className="px-3 py-2 text-xs">{c.loc_set_by || "-"}</td>
              <td className="px-3 py-2"><a href={`/operasyon/projeler/${c.project_id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-[color:var(--red)]"><MapPin className="h-3 w-3" /> Konum kaydet</a></td></tr>
          ))}
        </Table>
      )}
    </div>
  );
}
